import { Theme } from '@monorepo/relayably/theme'
import * as DocumentPicker from 'expo-document-picker'
import * as FileSystem from 'expo-file-system'
import { AudioLines, FileText, Paperclip, Send, X } from 'lucide-react-native'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native'
import { DashboardKnowledgeFilePayload } from '../types'

const MAX_ATTACHMENTS = 10

const ACCEPTED_MIME_TYPES = new Set([
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
])

const ACCEPTED_EXTENSIONS = ['.pdf', '.txt', '.doc', '.docx']
const SUPPORTED_FILE_TYPE_LABEL = 'PDF, TXT, DOC, DOCX'

const hexToRgba = (hex: string, alpha: number) => {
  const value = hex.replace('#', '')
  const expanded =
    value.length === 3
      ? value
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : value
  const r = parseInt(expanded.slice(0, 2), 16)
  const g = parseInt(expanded.slice(2, 4), 16)
  const b = parseInt(expanded.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

type BrowserRecognition = {
  start: () => void
  stop: () => void
  continuous: boolean
  interimResults: boolean
  onresult: ((event: any) => void) | null
  onend: (() => void) | null
}

const createBrowserRecognition = (): BrowserRecognition | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const Candidate = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
  if (!Candidate) {
    return null
  }

  const recognition = new Candidate()
  recognition.continuous = true
  recognition.interimResults = true
  return recognition as BrowserRecognition
}

const isAcceptedFile = (fileName: string, mimeType?: string | null) => {
  const lowerName = fileName.toLowerCase()
  return ACCEPTED_MIME_TYPES.has((mimeType || '').toLowerCase()) || ACCEPTED_EXTENSIONS.some(extension => lowerName.endsWith(extension))
}

const getDragState = (files: Array<{ name: string; type?: string | null }>) => {
  if (!files.length) {
    return null
  }

  return files.every(file => isAcceptedFile(file.name, file.type)) ? 'ready' : 'unsupported'
}

const readUriAsBase64 = async (uri: string) => {
  if (Platform.OS === 'web') {
    const response = await fetch(uri)
    const blob = await response.blob()

    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result).split(',').pop() ?? '')
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(blob)
    })
  }

  return FileSystem.readAsStringAsync(uri, {
    encoding: FileSystem.EncodingType.Base64,
  })
}

const toAttachmentPayload = async (asset: {
  name: string
  mimeType?: string | null
  uri: string
}): Promise<DashboardKnowledgeFilePayload | null> => {
  if (!isAcceptedFile(asset.name, asset.mimeType)) {
    return null
  }

  const base64 = await readUriAsBase64(asset.uri)

  return {
    name: asset.name.replace(/\.[^.]+$/, ''),
    fileName: asset.name,
    mimeType: asset.mimeType ?? 'application/octet-stream',
    base64,
  }
}

export const DirectScreen = ({
  theme,
  status,
  message,
  onSubmit,
}: {
  theme: Theme
  status: 'idle' | 'saving' | 'success' | 'error'
  message?: string | null
  onSubmit: (payload: { content: string; source: 'text' | 'voice'; attachments?: DashboardKnowledgeFilePayload[] }) => void
}) => {
  const [value, setValue] = useState('')
  const [recording, setRecording] = useState(false)
  const [inputSource, setInputSource] = useState<'text' | 'voice'>('text')
  const [attachments, setAttachments] = useState<DashboardKnowledgeFilePayload[]>([])
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const [dragState, setDragState] = useState<'ready' | 'unsupported' | null>(null)
  const recognitionRef = useRef<BrowserRecognition | null>(null)
  const styles = createStyles(theme)
  const canSubmit = Boolean(value.trim() || attachments.length) && status !== 'saving'
  const remainingSlots = MAX_ATTACHMENTS - attachments.length
  const canRecord = Platform.OS === 'web'

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop()
    }
  }, [])

  useEffect(() => {
    if (status === 'success') {
      setValue('')
      setAttachments([])
      setInputSource('text')
    }
  }, [status])

  const helperCopy = useMemo(() => {
    if (remainingSlots <= 0) {
      return 'You can include up to 10 documents per request.'
    }

    return `Attach up to ${remainingSlots} more ${remainingSlots === 1 ? 'document' : 'documents'} with this request.`
  }, [remainingSlots])

  const pushAttachments = async (
    nextFiles: Array<{
      name: string
      mimeType?: string | null
      uri: string
    }>
  ) => {
    const availableFiles = nextFiles.slice(0, Math.max(remainingSlots, 0))
    const nextPayloads = (await Promise.all(availableFiles.map(file => toAttachmentPayload(file)))).filter(
      (item): item is DashboardKnowledgeFilePayload => Boolean(item)
    )

    if (!nextPayloads.length) {
      return
    }

    setAttachments(current => [...current, ...nextPayloads].slice(0, MAX_ATTACHMENTS))
  }

  const handlePickAttachment = async () => {
    if (remainingSlots <= 0) {
      return
    }

    try {
      setUploadingAttachment(true)
      const result = await DocumentPicker.getDocumentAsync({
        copyToCacheDirectory: true,
        multiple: true,
        type: [
          'application/pdf',
          'text/plain',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ],
      })

      if (result.canceled) {
        return
      }

      await pushAttachments(
        result.assets.map(asset => ({
          name: asset.name,
          mimeType: asset.mimeType,
          uri: asset.uri,
        }))
      )
    } finally {
      setUploadingAttachment(false)
    }
  }

  const handleToggleRecording = () => {
    if (!recognitionRef.current) {
      recognitionRef.current = createBrowserRecognition()
    }

    const recognition = recognitionRef.current
    if (!recognition) {
      return
    }

    if (recording) {
      recognition.stop()
      setRecording(false)
      return
    }

    recognition.onresult = event => {
      const transcript = Array.from(event.results ?? [])
        .map((result: any) => result?.[0]?.transcript ?? '')
        .join(' ')

      setValue(transcript.trim())
      setInputSource('voice')
    }
    recognition.onend = () => {
      setRecording(false)
    }

    recognition.start()
    setRecording(true)
  }

  const handleDrop = async (event: any) => {
    if (Platform.OS !== 'web' || remainingSlots <= 0) {
      return
    }

    event.preventDefault?.()
    const droppedFiles = Array.from(event?.dataTransfer?.files ?? []) as File[]
    setDragState(null)
    if (!droppedFiles.length) {
      return
    }

    if (getDragState(droppedFiles) === 'unsupported') {
      setDragState('unsupported')
      return
    }

    try {
      setUploadingAttachment(true)
      const nextAssets = await Promise.all(
        droppedFiles.slice(0, remainingSlots).map(async file => {
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(String(reader.result).split(',').pop() ?? '')
            reader.onerror = () => reject(reader.error)
            reader.readAsDataURL(file)
          })

          return {
            name: file.name.replace(/\.[^.]+$/, ''),
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            base64,
          }
        })
      )

      setAttachments(current =>
        [
          ...current,
          ...nextAssets.filter(file => isAcceptedFile(file.fileName, file.mimeType)),
        ].slice(0, MAX_ATTACHMENTS)
      )
    } finally {
      setUploadingAttachment(false)
    }
  }

  const handleSubmit = () => {
    if (!canSubmit) {
      return
    }

    onSubmit({
      content: value.trim(),
      source: inputSource,
      attachments,
    })
  }

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps='handled'>
        <View style={styles.messageColumn}>
          <View style={styles.systemBubble}>
            <Text style={styles.systemLabel}>Relayably chat</Text>
            <Text style={styles.systemCopy}>
              Tell Relayably what your agents should know, how they should behave, or both. Documents you attach will be considered alongside the message.
            </Text>
          </View>

          {message ? (
            <View style={[styles.feedbackBubble, status === 'error' ? styles.feedbackBubbleError : undefined]}>
              <Text style={styles.feedbackText}>{message}</Text>
            </View>
          ) : null}
        </View>
      </ScrollView>

      <View
        style={[styles.composerShell, dragState ? styles.composerShellActive : undefined]}
        {...(Platform.OS === 'web'
          ? ({
              onDragOver: (event: any) => {
                event.preventDefault?.()
                if (remainingSlots > 0) {
                  const hoveredFiles = Array.from(event?.dataTransfer?.items ?? []).map((item: DataTransferItem) => ({
                    name: item.getAsFile?.()?.name ?? '',
                    type: item.type,
                  }))
                  setDragState(getDragState(hoveredFiles))
                }
              },
              onDragLeave: () => setDragState(null),
              onDrop: (event: any) => void handleDrop(event),
            } as any)
          : {})}
      >
        {dragState ? (
          <View pointerEvents='none' style={styles.dropOverlay}>
            <View style={[styles.dropOverlayCard, dragState === 'unsupported' ? styles.dropOverlayCardInvalid : undefined]}>
              <Text style={styles.dropOverlayTitle}>{dragState === 'unsupported' ? 'Unsupported file type.' : 'Drop file here'}</Text>
              <Text style={styles.dropOverlayBody}>Supported file types: {SUPPORTED_FILE_TYPE_LABEL}</Text>
            </View>
          </View>
        ) : null}
        {attachments.length ? (
          <View style={styles.attachmentRow}>
            {attachments.map((file, index) => (
              <View key={`${file.fileName}-${index}`} style={styles.attachmentChip}>
                <FileText color={theme.colors.inactive} size={15} />
                <Text numberOfLines={1} style={styles.attachmentChipText}>
                  {file.fileName}
                </Text>
                <Pressable
                  onPress={() => setAttachments(current => current.filter((_, currentIndex) => currentIndex !== index))}
                  disabled={status === 'saving'}
                  style={styles.attachmentRemove}
                >
                  <X color={theme.colors.inactive} size={14} />
                </Pressable>
              </View>
            ))}
          </View>
        ) : null}

        <View style={styles.inputRow}>
          <Pressable
            onPress={() => void handlePickAttachment()}
            disabled={uploadingAttachment || status === 'saving' || remainingSlots <= 0}
            style={[styles.iconButton, (uploadingAttachment || remainingSlots <= 0) && styles.iconButtonDisabled]}
          >
            {uploadingAttachment ? (
              <ActivityIndicator color={theme.colors.text} size='small' />
            ) : (
              <Paperclip color={theme.colors.text} size={18} />
            )}
          </Pressable>

          <TextInput
            multiline
            value={value}
            onChangeText={nextValue => {
              setValue(nextValue)
              setInputSource('text')
            }}
            editable={status !== 'saving'}
            onKeyPress={event => {
              if (Platform.OS !== 'web') {
                return
              }

              const nativeEvent = event.nativeEvent as typeof event.nativeEvent & {
                ctrlKey?: boolean
                metaKey?: boolean
              }

              if (nativeEvent.key === 'Enter' && (nativeEvent.metaKey || nativeEvent.ctrlKey)) {
                event.preventDefault?.()
                handleSubmit()
              }
            }}
            placeholder='Message Relayably…'
            placeholderTextColor={theme.colors.inactive}
            style={styles.input}
          />

          <Pressable
            onPress={handleToggleRecording}
            disabled={!canRecord || status === 'saving'}
            style={[
              styles.iconButton,
              (!canRecord || status === 'saving') && styles.iconButtonDisabled,
              recording && styles.iconButtonRecording,
            ]}
          >
            <AudioLines color={recording ? theme.colors.staticWhite : theme.colors.text} size={18} />
          </Pressable>

          <Pressable onPress={handleSubmit} disabled={!canSubmit} style={[styles.sendButton, !canSubmit && styles.sendButtonDisabled]}>
            {status === 'saving' ? <ActivityIndicator color={theme.colors.staticWhite} size='small' /> : <Send color={theme.colors.staticWhite} size={18} />}
          </Pressable>
        </View>

        <Text style={styles.helperText}>
          {dragState === 'unsupported' ? `Unsupported file type. Supported: ${SUPPORTED_FILE_TYPE_LABEL}.` : helperCopy}
        </Text>
      </View>
    </View>
  )
}

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    screen: {
      flex: 1,
      gap: 16,
    },
    content: {
      flexGrow: 1,
      paddingBottom: 24,
    },
    messageColumn: {
      gap: 14,
    },
    systemBubble: {
      alignSelf: 'flex-start',
      maxWidth: 720,
      borderRadius: 24,
      paddingHorizontal: 20,
      paddingVertical: 16,
      backgroundColor: hexToRgba(theme.colors.surface, 0.96),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.1),
      gap: 8,
    },
    systemLabel: {
      color: theme.colors.primary,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
    },
    systemCopy: {
      color: theme.colors.text,
      fontSize: 15,
      lineHeight: 24,
    },
    feedbackBubble: {
      alignSelf: 'flex-end',
      maxWidth: 720,
      borderRadius: 24,
      paddingHorizontal: 20,
      paddingVertical: 16,
      backgroundColor: hexToRgba(theme.colors.primary, 0.18),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.24),
    },
    feedbackBubbleError: {
      backgroundColor: hexToRgba(theme.colors.notification, 0.16),
      borderColor: hexToRgba(theme.colors.notification, 0.28),
    },
    feedbackText: {
      color: theme.colors.text,
      fontSize: 14,
      lineHeight: 22,
    },
    composerShell: {
      borderRadius: 28,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.14),
      backgroundColor: hexToRgba(theme.colors.surface, 0.98),
      padding: 14,
      gap: 12,
    },
    composerShellActive: {
      borderColor: hexToRgba(theme.colors.primary, 0.36),
      backgroundColor: hexToRgba(theme.colors.primary, 0.08),
    },
    dropOverlay: {
      ...StyleSheet.absoluteFillObject,
      zIndex: 5,
      borderRadius: 28,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: hexToRgba(theme.colors.background, 0.7),
      padding: 18,
    },
    dropOverlayCard: {
      width: '100%',
      maxWidth: 420,
      borderRadius: 24,
      paddingHorizontal: 20,
      paddingVertical: 18,
      alignItems: 'center',
      gap: 8,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.28),
      backgroundColor: hexToRgba(theme.colors.surface, 0.98),
    },
    dropOverlayCardInvalid: {
      borderColor: hexToRgba(theme.colors.notification, 0.4),
    },
    dropOverlayTitle: {
      color: theme.colors.text,
      fontSize: 18,
      fontWeight: '700',
      textAlign: 'center',
    },
    dropOverlayBody: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 20,
      textAlign: 'center',
    },
    attachmentRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
    },
    attachmentChip: {
      minWidth: 0,
      maxWidth: 280,
      minHeight: 40,
      borderRadius: 18,
      paddingLeft: 12,
      paddingRight: 8,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
      backgroundColor: hexToRgba(theme.colors.background, 0.8),
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    attachmentChipText: {
      flexShrink: 1,
      color: theme.colors.text,
      fontSize: 13,
      fontWeight: '600',
    },
    attachmentRemove: {
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
    },
    inputRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: 10,
    },
    input: {
      flex: 1,
      minHeight: 56,
      maxHeight: 180,
      borderRadius: 20,
      paddingHorizontal: 16,
      paddingVertical: 14,
      color: theme.colors.text,
      backgroundColor: hexToRgba(theme.colors.background, 0.64),
      fontSize: 15,
      lineHeight: 22,
    },
    iconButton: {
      width: 48,
      height: 48,
      borderRadius: 24,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: hexToRgba(theme.colors.background, 0.72),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
    },
    iconButtonDisabled: {
      opacity: 0.45,
    },
    iconButtonRecording: {
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },
    sendButton: {
      width: 48,
      height: 48,
      borderRadius: 24,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.primary,
    },
    sendButtonDisabled: {
      opacity: 0.45,
    },
    helperText: {
      color: theme.colors.inactive,
      fontSize: 12,
      lineHeight: 18,
      paddingHorizontal: 4,
    },
  })
